/**
 * Image utility functions extracted from Image.tsx, ImgFullScreen.tsx, Img.tsx, Images.tsx, and related components
 */
import { siteUrl } from "./constants";

/**
 * Prefix a Strapi media URL with the API origin — but only when it is
 * relative. Some media records carry absolute URLs (e.g. entered by hand
 * or left over from migration); naively concatenating the origin onto
 * those produced broken "https://…https://…" sources.
 */
export function mediaUrl(url?: string | null, base: string = siteUrl): string {
  if (!url) return "";
  return /^https?:\/\//.test(url) ? url : base + url;
}

/**
 * Determine image component type from type string
 */
export function getImageComponentType(
  type?: string
): "standard" | "fullScreen" | "fullWidth" | "text" | null {
  if (type && type.indexOf("standard") !== -1) return "standard";
  if (type === "fullScreen") return "fullScreen";
  if (type === "fullWidth") return "fullWidth";
  if (type === "text") return "text";
  return null;
}

/**
 * Parse caption string splitting on translation= marker
 */
export function parseCaptionArray(caption: string): string[] {
  return caption.indexOf("translation=") !== -1
    ? caption.split("translation=")
    : [caption];
}

/**
 * Resolve image URL preferring imgUrl over url
 */
export function resolveImageUrl(
  imgUrl?: string,
  url?: string
): string | undefined {
  return imgUrl || url;
}

/**
 * Get next index with wrap-around
 */
export function getNextIndex(current: number, total: number): number {
  return (current + 1) % total;
}

/**
 * Get previous index with wrap-around
 */
export function getPrevIndex(current: number, total: number): number {
  return (current + total - 1) % total;
}

/**
 * Get image size class based on width: <500 small, <900 medium, else ""
 */
export function getImgSizeClass(width: number): string {
  if (width < 500) return "small-centered-img";
  if (width < 900) return "medium-centered-img";
  return "";
}

/**
 * Determine caption render type based on array length
 */
export function getCaptionRenderType(
  captionArray?: string[]
): "dual" | "single" | "none" {
  if (Array.isArray(captionArray) && captionArray.length === 2) return "dual";
  if (Array.isArray(captionArray) && captionArray.length === 1) return "single";
  return "none";
}

/**
 * Get full screen background image style
 */
export function getFullScreenStyle(
  url?: string,
  isMobile?: boolean,
  height?: string
): Record<string, string> {
  if (!url) return {};
  return {
    backgroundImage: `url(${url})`,
    height: height || "100vh",
    backgroundAttachment: isMobile ? "scroll" : "fixed",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
  };
}

/**
 * Validate image text URL
 */
export function isValidImgTextUrl(url?: string): boolean {
  return url !== undefined && url !== "";
}

/**
 * Get images component type based on type and mobile flag
 */
export function getImagesComponentType(
  type?: string,
  isMobile?: boolean
): string | null {
  if (type === "carousel") return "carousel";
  if (type === "movingText" && !isMobile) return "movingText";
  if (type === "movingText" && isMobile) return "mobileSequence";
  if (type === "sequence") return "sequence";
  return null;
}

/**
 * Get step height for moving text images
 */
export function getStepHeight(
  stepIndex: number,
  hasCaption: boolean
): string {
  return stepIndex === 0 && hasCaption ? "200vh" : "100vh";
}

/**
 * Get image size class from dimensions and optional type override
 */
export function getImgSizeFromDimensions(
  width: number,
  type?: string
): string {
  if (width < 1000 || type === "standardSmall") return "small-centered-img";
  if (type === "standardMedium") return "medium-centered-img";
  return "";
}

/**
 * Check if this is the first timeline item
 */
export function isFirstTimelineItem(index: number): boolean {
  return index === 0;
}
